/**
 * Memorial workspace — tribute moderation (pending, approved, hidden, spam).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Ban, Check, EyeOff, Loader2, MessageSquare, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { getApiBase } from '../../utils/apiBase';
import { TRIBUTE_REACTION_OPTIONS } from './LeaveTributeForm';
import { toast } from 'sonner';

const API = getApiBase();

function formatTributeDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function reactionLabel(key) {
  return TRIBUTE_REACTION_OPTIONS.find((r) => r.key === key)?.label || key;
}

function TributeCard({ tribute, acting, onModerate }) {
  const date = formatTributeDate(tribute.created_at);
  return (
    <li className="rounded-xl border border-border/50 bg-muted/10 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{tribute.author_name}</p>
          {tribute.relationship ? (
            <p className="text-xs text-muted-foreground">{tribute.relationship}</p>
          ) : null}
          {tribute.author_email ? (
            <p className="text-xs text-muted-foreground/80">Email: {tribute.author_email}</p>
          ) : null}
          {date ? <p className="mt-0.5 text-[10px] text-muted-foreground/80">{date}</p> : null}
          {tribute.reaction_type ? (
            <p className="mt-1 text-[10px] text-muted-foreground">Remembrance: {reactionLabel(tribute.reaction_type)}</p>
          ) : null}
        </div>
      </div>
      <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{tribute.message}</p>
      {onModerate ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {tribute.status !== 'approved' ? (
            <Button
              type="button"
              size="sm"
              className="rounded-full"
              disabled={acting}
              onClick={() => onModerate(tribute.id, 'approved')}
            >
              {acting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
              Approve
            </Button>
          ) : null}
          {tribute.status !== 'hidden' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={acting}
              onClick={() => onModerate(tribute.id, 'hidden')}
            >
              <EyeOff className="mr-1 h-3 w-3" />
              Hide
            </Button>
          ) : null}
          {tribute.status !== 'spam' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full text-red-700"
              disabled={acting}
              onClick={() => onModerate(tribute.id, 'spam')}
            >
              <Ban className="mr-1 h-3 w-3" />
              Mark spam
            </Button>
          ) : null}
          {tribute.status === 'hidden' || tribute.status === 'spam' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full text-xs"
              disabled={acting}
              onClick={() => onModerate(tribute.id, 'approved')}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Restore
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function MemorialTributesPanel({
  memorialId,
  form,
  setForm,
  onUpdated,
}) {
  const [loading, setLoading] = useState(false);
  const [tributes, setTributes] = useState([]);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    if (!memorialId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/memorials-api/${memorialId}/tributes`);
      setTributes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load tributes.');
    } finally {
      setLoading(false);
    }
  }, [memorialId]);

  useEffect(() => {
    load();
  }, [load]);

  const moderate = async (tributeId, status) => {
    setActingId(tributeId);
    try {
      await axios.patch(`${API}/memorials-api/${memorialId}/tributes/${tributeId}`, { status });
      const label = status === 'approved' ? 'approved' : status === 'spam' ? 'marked as spam' : 'hidden';
      toast.success(`Tribute ${label}.`);
      await load();
      onUpdated?.();
    } catch {
      toast.error('Could not update tribute.');
    } finally {
      setActingId(null);
    }
  };

  const pending = tributes.filter((t) => t.status === 'pending');
  const approved = tributes.filter((t) => t.status === 'approved');
  const hidden = tributes.filter((t) => t.status === 'hidden');
  const spam = tributes.filter((t) => t.status === 'spam');

  return (
    <div className="space-y-5" data-testid="memorial-tributes-panel">
      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <h3 className="font-serif text-base font-light text-[#1c1917]">Guestbook settings</h3>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/90">
          New tributes stay pending until you approve them. Emails are never shown publicly.
        </p>
        <div className="mt-4 space-y-4">
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Allow tributes</span>
            <Switch
              checked={form.allow_tributes}
              onCheckedChange={(v) => setForm((p) => ({ ...p, allow_tributes: v }))}
            />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Moderate before publishing</span>
            <Switch
              checked={form.moderate_tributes}
              onCheckedChange={(v) => setForm((p) => ({ ...p, moderate_tributes: v }))}
            />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Require email for tributes</span>
            <Switch
              checked={form.require_email_for_tributes}
              onCheckedChange={(v) => setForm((p) => ({ ...p, require_email_for_tributes: v }))}
            />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Allow quiet reactions</span>
            <Switch
              checked={form.enable_reactions}
              onCheckedChange={(v) => setForm((p) => ({ ...p, enable_reactions: v }))}
            />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Show reaction counts publicly</span>
            <Switch
              checked={form.show_reaction_counts}
              onCheckedChange={(v) => setForm((p) => ({ ...p, show_reaction_counts: v }))}
            />
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <h3 className="font-serif text-base font-light text-[#1c1917]">Tributes</h3>
        <p className="mt-1.5 text-xs text-muted-foreground">Approve, hide, or mark spam on messages from visitors.</p>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-4 space-y-6">
            {pending.length > 0 ? (
              <section>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-amber-700">
                  Pending review ({pending.length})
                </p>
                <ul className="space-y-3">
                  {pending.map((t) => (
                    <TributeCard
                      key={t.id}
                      tribute={t}
                      acting={actingId === t.id}
                      onModerate={moderate}
                    />
                  ))}
                </ul>
              </section>
            ) : null}

            <section>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Approved ({approved.length})
              </p>
              {approved.length === 0 ? (
                <p className="text-sm text-muted-foreground">Approved messages will appear on the public memorial page.</p>
              ) : (
                <ul className="space-y-3">
                  {approved.map((t) => (
                    <TributeCard
                      key={t.id}
                      tribute={t}
                      acting={actingId === t.id}
                      onModerate={moderate}
                    />
                  ))}
                </ul>
              )}
            </section>

            {hidden.length > 0 ? (
              <section>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Hidden ({hidden.length})
                </p>
                <ul className="space-y-3">
                  {hidden.map((t) => (
                    <TributeCard
                      key={t.id}
                      tribute={t}
                      acting={actingId === t.id}
                      onModerate={moderate}
                    />
                  ))}
                </ul>
              </section>
            ) : null}

            {spam.length > 0 ? (
              <section>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-red-700/80">
                  Spam ({spam.length})
                </p>
                <ul className="space-y-3">
                  {spam.map((t) => (
                    <TributeCard
                      key={t.id}
                      tribute={t}
                      acting={actingId === t.id}
                      onModerate={moderate}
                    />
                  ))}
                </ul>
              </section>
            ) : null}

            {pending.length === 0 && approved.length === 0 && hidden.length === 0 && spam.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                <MessageSquare className="h-8 w-8 opacity-40" />
                <p className="text-sm">When visitors leave messages, they&apos;ll wait here for your review.</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default MemorialTributesPanel;
